import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Listbox } from '@headlessui/react';
import { XMarkIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import TurndownService from 'turndown';

import CoverImageUploader from '@/components/blog/CoverImageUploader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

import { toast } from '@/hooks/use-toast';
import { handleServerError } from '@/utils/handle-server-error';
import {
  list_blog_categories,
  create_blog_category,
  deleteBlogCategory,
  list_blog_tags,
  create_blog_tag,
  create_superadmin_blog_preview,
  update_superadmin_blog_post,
  BlogCategory,
  BlogTag,
  BlogDomain,
  list_blog_domains,
} from '@/api/blog';

export default function BlogCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedTags, setSelectedTags] = useState<BlogTag[]>([]);

  /* =========================
     Blog Fields
  ========================= */
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [isRTL, setIsRTL] = useState(false);
  const [saving, setSaving] = useState(false);

  /* =========================
     Domains
  ========================= */
  const [domains, setDomains] = useState<BlogDomain[]>([]);
  const [domainName, setDomainName] = useState<string>('');

  /* =========================
     Categories
  ========================= */
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  /* =========================
     Tags
  ========================= */
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  /* =========================
     Cover Image Ref
  ========================= */
  interface CoverUploaderRef {
    uploadImage: (id?: string) => Promise<string | null>;
    uploading: boolean;
    blogId?: string;
  }

  const coverRef = useRef<CoverUploaderRef>(null);
  const isUploading = coverRef.current?.uploading ?? false;

  /* =========================
     TipTap Editor
  ========================= */
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Heading.configure({ levels: [1, 2, 3] }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    editorProps: {
      attributes: {
        class:
          'min-h-[400px] p-4 border rounded-md bg-white focus:outline-none prose max-w-none',
        dir: isRTL ? 'rtl' : 'ltr',
      },
      handleTextInput(view, _from, _to, text) {
        const currentLength = view.state.doc.textContent.length;
        return currentLength + text.length > CONTENT_MAX;
      },
      handlePaste(view, event) {
        const pasteText = event.clipboardData?.getData('text/plain') ?? '';
        const currentLength = view.state.doc.textContent.length;
        return currentLength + pasteText.length > CONTENT_MAX;
      },
    },
    onUpdate: ({ editor }) => {
      setContent(editor.getHTML());
    },
  });


  /* =========================
     Fetch Domains, Categories & Tags
  ========================= */
  const fetchDomains = async () => {
    try {
      setDomains(await list_blog_domains());
    } catch (error) {
      handleServerError(error);
    }
  };

  const fetchCategories = async (dName?: string) => {
    try {
      setCategories(await list_blog_categories(dName));
    } catch (error) {
      handleServerError(error);
    }
  };

  const fetchTags = async (dName?: string) => {
    try {
      setTags(await list_blog_tags(dName));
    } catch (error) {
      handleServerError(error);
    }
  };

  useEffect(() => {
    fetchDomains();
    // Default fetch without domain filter initially
    fetchCategories();
    fetchTags();
  }, []);

  // Re-fetch categories and tags when domain changes
  useEffect(() => {
    if (domainName) {
      fetchCategories(domainName);
      fetchTags(domainName);
      // Reset selections when domain changes to prevent cross-domain issues
      setCategoryId(null);
      setSelectedTags([]);
      setTagIds([]);
    } else {
      fetchCategories();
      fetchTags();
    }
  }, [domainName]);

  /* =========================
     Category CRUD
  ========================= */
  const handleCreateCategory = async () => {
    if (!newCategory.trim() || creatingCategory) return;
    if (!domainName) {
      toast({ title: t('error'), description: t('select_domain_first', 'Please select a domain first'), variant: 'destructive' });
      return;
    }
    try {
      setCreatingCategory(true);
      const created = await create_blog_category(newCategory.trim(), domainName);
      await fetchCategories(domainName);
      setCategoryId(created.id);
      setNewCategory('');
    } catch (error) {
      handleServerError(error);
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Delete this category?')) return;
    try {
      await deleteBlogCategory(id);
      if (categoryId === id) setCategoryId(null);
      await fetchCategories();
    } catch (error) {
      handleServerError(error);
    }
  };

  /* =========================
     Tag CRUD
  ========================= */
  const handleCreateTag = async () => {
    if (!newTag.trim() || creatingTag) return;
    if (!domainName) {
      toast({ title: t('error'), description: t('select_domain_first', 'Please select a domain first'), variant: 'destructive' });
      return;
    }
    try {
      setCreatingTag(true);
      const created = await create_blog_tag(newTag.trim(), domainName);
      await fetchTags(domainName);
      setTagIds([...tagIds, created.id]);
      setNewTag('');
    } catch (error) {
      handleServerError(error);
    } finally {
      setCreatingTag(false);
    }
  };

  /* =========================
     Preview Handler
  ========================= */
  const handlePreview = async () => {
    if (!title || !content || !domainName) {
      toast({ title: t('error'), description: t('title_content_domain_required', 'Title, content, and domain are required'), variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);

      // Convert HTML to Markdown
      const turndownService = new TurndownService();
      const markdownContent = turndownService.turndown(content);

      const draft = await create_superadmin_blog_preview({
        title,
        summary,
        content_markdown: markdownContent, // Send Markdown
        category_id: categoryId || null,
        hashtag_ids: selectedTags.map(t => t.id),
        cover_image: null,
        language: isRTL ? 'ar' : 'fr',
        domain_name: domainName,
      });
      console.log('Draft created:', draft);

      if (coverRef.current) {
        // Pass draft.id to uploadImage
        coverRef.current.blogId = draft.id;
      }

      // Pass draft.id directly to uploadImage
      const coverUrl = await coverRef.current?.uploadImage(draft.id);
      console.log('Cover URL:', coverUrl);

      if (coverUrl) {
        await update_superadmin_blog_post(draft.id, {
          cover_image: coverUrl,
        });
      }


      localStorage.setItem('preview_blog_id', draft.id);
      navigate({ to: '/super-admin-blog-preview', search: { id: draft.id } });
    } catch (error) {
      handleServerError(error);
    } finally {
      setSaving(false);
    }
  };


  /* =========================
     Render
  ========================= */

  const TITLE_RECOMMENDED_MAX = 60;
  const SUMMARY_RECOMMENDED_MAX = 160;
  const CONTENT_MAX = 50000; // Increased limit for long articles
  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <CoverImageUploader ref={coverRef} blogId="" />

      <Card>
        <CardHeader>
          <CardTitle>{t('create_blog')}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Domain */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('blog_domain', 'Domain')} <span className="text-red-500">*</span></label>
            <Listbox value={domainName} onChange={setDomainName}>
              <div className="relative mt-1">
                <Listbox.Button className="relative w-full md:w-72 cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left">
                  {domainName || t('select_domain', 'Select a domain')}
                  <ChevronUpDownIcon className="h-5 w-5 absolute right-2 top-2.5 text-gray-400" />
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full bg-white border rounded shadow overflow-auto">
                  {domains.map((dom) => (
                    <Listbox.Option
                      key={dom.id}
                      value={dom.name}
                      className={({ active }) => `cursor-pointer select-none relative py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                    >
                      {dom.name}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
            <p className="text-xs text-muted-foreground">{t('domain_selection_hint', 'Categories and Tags are specific to the selected domain.')}</p>
          </div>

          {/* Title */}
          <div className="border-t pt-6">
            <div className="flex justify-between">
              <label className="text-sm font-medium">{t('blog_title')}</label>
              <span className={`text-xs ${title.length > TITLE_RECOMMENDED_MAX ? 'text-orange-500' : 'text-gray-500'}`}>
                {title.length} / {TITLE_RECOMMENDED_MAX} (SEO)
              </span>
            </div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">
              {t('seo_title_recommendation', 'Recommended: 50-60 characters for best SEO results.')}
            </p>
          </div>

          {/* Summary */}
          <div>
            <div className="flex justify-between">
              <label className="text-sm font-medium">{t('blog_summary')}</label>
              <span className={`text-xs ${summary.length > SUMMARY_RECOMMENDED_MAX ? 'text-orange-500' : 'text-gray-500'}`}>
                {summary.length} / {SUMMARY_RECOMMENDED_MAX} (SEO)
              </span>
            </div>
            <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">
              {t('seo_summary_recommendation', 'Recommended: 150-160 characters for best SEO results.')}
            </p>
          </div>

          {/* Language */}
          <div className="flex items-center gap-3">
            <Switch checked={isRTL} onCheckedChange={setIsRTL} />
            <span className="text-sm">
              {isRTL ? t('Arabe') : t('Français')}
            </span>
          </div>

          {/* Editor */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('content')}</label>

            <div className="flex flex-wrap gap-2 border rounded-md p-2 bg-gray-50">
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleBold().run()}>Bold</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleItalic().run()}>Italic</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleUnderline().run()}>Underline</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}>H1</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>H2</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleBulletList().run()}>• List</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleOrderedList().run()}>1. List</Button>
            </div>

            <EditorContent editor={editor} />
          </div>
          <p
            className={`text-sm text-right ${editor?.getText().length >= CONTENT_MAX
              ? 'text-red-500'
              : 'text-gray-500'
              }`}
          >
            {editor?.getText().length || 0} / {CONTENT_MAX}
          </p>
          {/* Category (UNCHANGED) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('category_b')}</label>

            <Listbox value={categoryId} onChange={setCategoryId}>
              <div className="relative mt-1">
                <Listbox.Button className="relative w-72 cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left">
                  {categoryId
                    ? categories.find((c) => c.id === categoryId)?.name
                    : t('select_category_b')}
                  <ChevronUpDownIcon className="h-5 w-5 absolute right-2 top-2.5" />
                </Listbox.Button>

                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full bg-white border rounded shadow">
                  {categories.map((cat) => (
                    <Listbox.Option
                      key={cat.id}
                      value={cat.id}
                      className="flex justify-between px-3 py-2 hover:bg-gray-100"
                    >
                      {cat.name}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(cat.id);
                        }}
                      >
                        <XMarkIcon className="h-4 w-4 text-red-500" />
                      </button>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>

            <div className="flex gap-2">
              <Input
                placeholder={t('new_category')}
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-56"
              />
              <Button onClick={handleCreateCategory} disabled={creatingCategory}>
                {creatingCategory ? t('creating_b') : t('create_b')}
              </Button>
            </div>
          </div>

          {/* Tags (UNCHANGED) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('tags')}</label>

            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => {
                const selected = selectedTags.some(t => t.id === tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => {
                      if (selected) {
                        setSelectedTags(selectedTags.filter(t => t.id !== tag.id));
                      } else {
                        setSelectedTags([...selectedTags, tag]);
                      }
                    }}
                    className={`px-2 py-1 rounded-full border ${selected ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-800'
                      }`}
                  >
                    {tag.name}
                  </button>
                );
              })}

            </div>

            <div className="flex gap-2">
              <Input
                placeholder={t('new_tag')}
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                className="w-56"
              />
              <Button onClick={handleCreateTag} disabled={creatingTag}>
                {creatingTag ? t('creating_t') : t('create_t')}
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate({ to: '/super-admin-blog' })}>
              {t('cancel')}
            </Button>
            <Button onClick={handlePreview} disabled={saving || isUploading}>
              {saving ? t('previewing') : t('preview')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
